// E11 R1 (WI 611) — the platform Global-Admin impersonation grants page. Types mirror the merged USERS
// contract exactly (LogikSpark.Users). The list DTO (ImpersonationGrantSummaryDto) is deliberately IDs-only
// — it carries NO display names and NO target tenant, and Status is server-computed (no persisted column).

// The four server-computed grant states (usp_GetImpersonationGrants CASE, priority order Revoked > Expired >
// Used > Active). Used = an in-flight (EndedAt IS NULL) session exists under the grant.
// WI 619 break-glass bulk-revoke: the exact phrase the operator must type to confirm ending ALL sessions.
// Mirrors the server's EndAllActiveImpersonationSessionsCommandValidator.RequiredConfirmPhrase (case-sensitive)
// — the SPA gates on it as friction; the server re-validates and 400s a wrong value before any write.
export const BULK_REVOKE_CONFIRM_PHRASE = 'END-ALL-IMPERSONATION'

export type ImpersonationGrantStatus = 'Active' | 'Used' | 'Expired' | 'Revoked'

export const IMPERSONATION_GRANT_STATUSES: ImpersonationGrantStatus[] = [
  'Active',
  'Used',
  'Expired',
  'Revoked',
]

// A grant row, mapping ImpersonationGrantSummaryDto 1:1 (aliases match usp_GetImpersonationGrants' SELECT).
// StartsAt is the grant's start instant (the list is ordered StartsAt DESC — the "granted at" the AC calls
// for). There is no GrantedAt / no grantor-grantee-target names / no tenant on this DTO.
export interface ImpersonationGrantSummary {
  grantId: number
  grantedByUserId: number
  granteeUserId: number
  targetUserId: number
  reason: string
  startsAt: string
  expiresAt: string
  revokedAt?: string | null
  status: ImpersonationGrantStatus
  // WI 611 display enrichment — the server (usp_GetImpersonationGrants) composes these with CONCAT, so a
  // grant with NULL name parts can arrive as whitespace; the table falls back to "User #{id}" when blank.
  grantorDisplayName: string
  granteeDisplayName: string
  targetDisplayName: string
  targetEmail: string
  targetTenantName: string
}

// GET v1/impersonation/grants payload (ImpersonationGrantsResult): up to 500 rows + a HasMore cap flag.
export interface ImpersonationGrantsResult {
  grants: ImpersonationGrantSummary[]
  hasMore: boolean
}

// One session row under a grant (WI 612 row-expansion), mapping ImpersonationGrantSessionRow. endedAt /
// endReason are null while the session is active; endedByDisplayName is the acting user's composed name.
export interface ImpersonationGrantSession {
  sessionId: number
  impersonationGrantId: number
  startedAt: string
  endedAt?: string | null
  endReason?: number | null
  endedByUserId: number
  endedByDisplayName: string
}

// GET v1/impersonation/grants/{id}/sessions payload (GrantWithSessionsResult): the grant + its sessions.
export interface GrantWithSessions {
  grant: ImpersonationGrantSummary
  sessions: ImpersonationGrantSession[]
}

// GET v1/impersonation/grants query params. status is a CSV of the four states; all optional.
export interface GetImpersonationGrantsParams {
  status?: string
  tenantId?: number
  granteeId?: number
}

// POST v1/impersonation/grants body (InsertImpersonationGrantRequest). The grantor is the caller server-side
// (never on the wire), so only these four fields are sent. ExpiresAt is a UTC ISO string (<= 8h from now).
export interface CreateImpersonationGrantRequest {
  granteeUserId: number
  targetUserId: number
  reason: string
  expiresAt: string
}

// Typed result of a create-grant failure. The server maps every 50200-50204 sproc THROW to HTTP 400 with a
// { code: 'INVALID_GRANT_REQUEST', message } envelope; the 8-hour ceiling (50204) is detected by the message
// ("...exceeds the 8-hour maximum...") and rendered inline on the modal. Everything else is toasted.
export type CreateGrantApiError =
  | { kind: 'exceeds-max-duration'; message: string }
  | { kind: 'invalid-grant'; message: string }
  | { kind: 'unknown'; status?: number; message: string }

// GET v1/me payload (MeResponse). USERS-owned (not a CORE type). R1 only needs isGlobalAdmin for the guard
// and activePersona.id to default the grantee to 'Self'; the fuller shape is modelled for the later banner.
export interface MeAuthenticatedHuman {
  oid: string
  email: string
}

export interface MeTenant {
  id: number
  subdomain: string
  displayName: string
}

export interface MeActivePersona {
  id: number
  userTypeId: number
  isGlobalAdmin: boolean
  isAccountOwner: boolean
  tenantId: number
  customerId?: number | null
}

export interface MeImpersonation {
  sessionId: number
  grantId: number
  targetUserId: number
  targetDisplayName: string
  targetEmail: string
  targetTenantName: string
  expiresAt: string
  reason: string
}

export interface MeResponse {
  authenticatedHuman: MeAuthenticatedHuman
  tenant: MeTenant
  activePersona: MeActivePersona
  isGlobalAdmin: boolean
  impersonation?: MeImpersonation | null
}

// E11 R5 (WI 616) — one audited write attributed to an impersonation session, for the banner's activity
// drawer (a row of GET v1/impersonation/sessions/{id}/activity, most-recent-first). entity is the Users-DB
// table (e.g. 'User', 'Role', 'UserGroupRoleAllocation'); displayLabel is the entity's name / composed
// person-name, or the id as text for the label-less allocation/detail tables; timestamp is the write instant
// (UTC ISO).
export interface ImpersonationSessionActivity {
  entity: string
  entityId: number
  displayLabel: string
  timestamp: string
}
